import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  /** Wider variant for forms with more than a couple of fields. */
  wide?: boolean;
  /** Set false for a decision the user must answer explicitly. */
  dismissible?: boolean;
}

/**
 * A modal at the top elevation.
 *
 * Focus is trapped while open and restored to whatever opened it on close,
 * because a dialog that drops focus back to <body> strands keyboard and screen
 * reader users at the top of the page with no way back to their place.
 *
 * ── IT IS PORTALLED TO <body>, AND THAT IS NOT TIDINESS ───────────────────
 *
 * The backdrop is `position: fixed; inset: 0`, which covers the viewport ONLY
 * while no ancestor is a containing block for fixed positioning. An ancestor
 * with `filter`, `backdrop-filter`, `transform`, `will-change` or `contain`
 * becomes one — and `.panel`, the workhorse surface of every screen in this
 * console, carries `backdrop-filter: blur(20px) saturate(180%)`.
 *
 * So a dialog written inside a `<Panel>` — the obvious place to put it, beside
 * the button that opens it — silently stopped being a modal. Measured: the
 * backdrop rendered 982×239 at (265, 323) instead of 1280×900 at (0, 0), its
 * stacking context was the panel's, and the two panels further down the page
 * PAINTED OVER ITS LOWER HALF. The screen looked like a half-drawn dialog with
 * the page bleeding through it, and the primary action was behind other content.
 *
 * `Shell.tsx` already worked around this by hand for the sticky header, with a
 * comment saying every other dialog is mounted at the root. That was true until
 * it was not, and relying on every future call site to know where it may place a
 * dialog is not a guarantee. Portalling makes the position of the JSX
 * irrelevant, which is the property a modal should have.
 *
 * React context still reaches a portalled child, so nothing a dialog consumes
 * changes. The only thing that moves is where the nodes land in the DOM.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  actions,
  wide = false,
  dismissible = true,
}: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  // Held in a ref, not in the dependency array of the effect below. Every call
  // site passes an inline arrow, so a fresh onClose each render tore that effect
  // down and re-ran it on every keystroke: the cleanup restored focus to the
  // opener and the new run pulled it back to the panel, swallowing every
  // character after the first. The handler reads this at event time, which is
  // always after the commit that updated it.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    panel?.focus();

    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    // The page behind must not scroll under the dialog.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreTo.current?.focus?.();
    };
  }, [open, dismissible]);

  const onBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (dismissible && e.target === e.currentTarget) onClose();
    },
    [dismissible, onClose],
  );

  /*
   * `document` IS READ DURING RENDER, AND THAT IS SAFE HERE FOR ONE REASON.
   *
   * Two of the four apps are Next, which server-renders even a `'use client'`
   * component, and on the server there is no `document`. The usual dance is a
   * `mounted` flag set in an effect — but that is `setState` inside an effect,
   * which the lint rule rightly objects to, and it costs a second render of
   * every dialog in the platform to solve a problem that is not reachable.
   *
   * Not reachable because `open` is FALSE AT HYDRATION at every call site: a
   * dialog is opened by a person pressing something, so the server and the first
   * client render both take the `!open` branch and emit nothing. By the time
   * `open` turns true, the component is only ever running in a browser.
   *
   * The `typeof` guard is kept anyway, so the day somebody renders one open from
   * a URL parameter it degrades to "no dialog in the server's HTML" rather than
   * to a crash on `document`.
   */
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onBackdrop}>
      <div
        ref={panelRef}
        className={wide ? 'dialog dialog-wide' : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>
        {children ? <div className="dialog-body">{children}</div> : null}
        {actions ? <div className="dialog-actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
